import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { isNativeApp } from "@/lib/platform";
import { ArrowLeftRight, Compass, Home, MessageSquare, User } from "lucide-react";

/**
 * iOS-style bottom tab bar visible only inside the Capacitor webview.
 *
 * Order (left → right):
 *   Home (Dashboard) · Browse · Matches (highlighted centre) · Messages · Profile
 *
 * Matches is the primary reason people open the app, so it lives in the
 * highlighted centre pill. The icon is a horizontal double-arrow — the
 * universal "swap" symbol.
 */
export default function BottomTabBar() {
    const { user } = useAuth();
    const location = useLocation();

    if (!isNativeApp()) return null;

    const hideOn = ["/", "/exchange-buddy", "/onboarding"];
    if (hideOn.includes(location.pathname)) return null;

    const items = [
        { to: user ? "/dashboard" : "/login", label: "Home",     icon: Home,           testId: "bottom-nav-home",     requiresAuth: true },
        { to: "/browse",                       label: "Browse",   icon: Compass,        testId: "bottom-nav-browse" },
        { to: "/matches",                      label: "Matches",  icon: ArrowLeftRight, testId: "bottom-nav-matches",  requiresAuth: true, highlight: true },
        { to: "/messages",                     label: "Messages", icon: MessageSquare,  testId: "bottom-nav-messages", requiresAuth: true },
        { to: user ? "/profile" : "/login",   label: user ? "Profile" : "Log in", icon: User, testId: "bottom-nav-profile" },
    ];

    return (
        <nav
            data-testid="bottom-tab-bar"
            className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-border/70 shadow-[0_-4px_20px_rgba(0,0,0,0.04)]"
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
            <div className="flex items-stretch justify-around px-2 pt-1.5 pb-1.5">
                {items.map((item) => {
                    const active =
                        location.pathname === item.to ||
                        (item.to === "/browse" && location.pathname.startsWith("/rooms/"));
                    if (item.requiresAuth && !user) {
                        return (
                            <NavLink
                                key={item.testId}
                                to="/login"
                                data-testid={item.testId}
                                className="flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 py-1 opacity-40"
                            >
                                {item.highlight ? <PillIcon Icon={item.icon} active={false} /> : <item.icon className="w-6 h-6" strokeWidth={2} />}
                                <span className="text-[10px] font-semibold tracking-tight leading-none">{item.label}</span>
                            </NavLink>
                        );
                    }
                    return (
                        <NavLink
                            key={item.testId}
                            to={item.to}
                            data-testid={item.testId}
                            className={`flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 py-1 active:scale-95 transition-transform ${active ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}
                        >
                            {item.highlight ? (
                                <PillIcon Icon={item.icon} active={active} />
                            ) : (
                                <item.icon className="w-6 h-6" strokeWidth={active ? 2.5 : 2} />
                            )}
                            <span className={`text-[10px] tracking-tight leading-none ${active ? "font-bold" : "font-medium"}`}>
                                {item.label}
                            </span>
                        </NavLink>
                    );
                })}
            </div>
        </nav>
    );
}

function PillIcon({ Icon, active }) {
    return (
        <div
            className={`w-11 h-11 -mt-4 mb-0.5 rounded-full flex items-center justify-center shadow-lg bg-primary text-primary-foreground ${active ? "scale-105" : ""} transition-transform duration-200`}
        >
            <Icon className="w-6 h-6" strokeWidth={2.4} />
        </div>
    );
}
